import Inventory2RoundedIcon from '@mui/icons-material/Inventory2Rounded';
import ManageSearchRoundedIcon from '@mui/icons-material/ManageSearchRounded';
import TravelExploreRoundedIcon from '@mui/icons-material/TravelExploreRounded';
import { Stack } from '@mui/material';

import { useCatalogSettings, useUpdateCatalogSettings } from '../../hooks';
import { toSettingsUpdate, useDomainPermissions, useFeedback, useSettingsForm } from '../../lib';
import {
  ChipListField,
  FeedbackSnackbar,
  SettingsCard,
  SettingsScaffold,
  TextInputField,
} from '../components';

/** Storefront catalog behaviour. */
export function CatalogSettingsPage() {
  const { canView, canManage } = useDomainPermissions('catalog');
  const query = useCatalogSettings();
  const update = useUpdateCatalogSettings();
  const form = useSettingsForm(query.data);
  const { feedback, success, error: notifyError, clear } = useFeedback();

  const handleSave = () => {
    if (!form.draft) {
      return;
    }
    update.mutate(toSettingsUpdate(form.draft), {
      onSuccess: () => success('Catalog settings saved.'),
      onError: (error) => {
        notifyError(error.message);
        if (error.status === 409) {
          void query.refetch();
        }
      },
    });
  };

  return (
    <SettingsScaffold
      title="Catalog settings"
      subtitle="Publication, visibility, search behaviour, SEO defaults and media rules."
      icon={<Inventory2RoundedIcon />}
      query={query}
      canView={canView}
      canManage={canManage}
      audit={query.data}
      dirty={form.dirty}
      saving={update.isPending}
      onSave={handleSave}
      onReset={form.reset}
      feedback={<FeedbackSnackbar feedback={feedback} onClose={clear} />}
    >
      {() => {
        const draft = form.draft;
        if (!draft) {
          return null;
        }
        const disabled = !canManage;
        return (
          <Stack spacing={2.5}>
            <SettingsCard title="Visibility & publication" icon={<Inventory2RoundedIcon />}>
              <TextInputField
                label="Product publication rule"
                value={draft.productPublicationRule}
                onChange={(value) => form.setField('productPublicationRule', value)}
                disabled={disabled}
              />
              <TextInputField
                label="Category visibility rule"
                value={draft.categoryVisibilityRule}
                onChange={(value) => form.setField('categoryVisibilityRule', value)}
                disabled={disabled}
              />
              <TextInputField
                label="Attribute display rule"
                value={draft.attributeDisplayRule}
                onChange={(value) => form.setField('attributeDisplayRule', value)}
                disabled={disabled}
              />
              <TextInputField
                label="Availability display"
                value={draft.availabilityDisplay}
                onChange={(value) => form.setField('availabilityDisplay', value)}
                disabled={disabled}
              />
              <ChipListField
                label="Media rules"
                description="Constraints applied to product media."
                value={draft.mediaRules}
                onChange={(value) => form.setField('mediaRules', value)}
                disabled={disabled}
              />
            </SettingsCard>

            <SettingsCard title="Search" icon={<ManageSearchRoundedIcon />}>
              <TextInputField
                label="Search behaviour"
                value={draft.searchBehavior}
                onChange={(value) => form.setField('searchBehavior', value)}
                disabled={disabled}
              />
            </SettingsCard>

            <SettingsCard
              title="SEO defaults"
              description="Templates applied when a product/category has no explicit SEO."
              icon={<TravelExploreRoundedIcon />}
            >
              <TextInputField
                label="Title template"
                value={draft.seoDefaults.titleTemplate ?? ''}
                onChange={(value) =>
                  form.setField('seoDefaults', {
                    ...draft.seoDefaults,
                    titleTemplate: value || null,
                  })
                }
                disabled={disabled}
              />
              <TextInputField
                label="Description template"
                value={draft.seoDefaults.descriptionTemplate ?? ''}
                onChange={(value) =>
                  form.setField('seoDefaults', {
                    ...draft.seoDefaults,
                    descriptionTemplate: value || null,
                  })
                }
                disabled={disabled}
                multiline
                rows={2}
              />
            </SettingsCard>
          </Stack>
        );
      }}
    </SettingsScaffold>
  );
}
